import { Injectable } from '@angular/core';
import { NotificationService } from './notification.service';

@Injectable({
  providedIn: 'root'
})
export class BrowserUtilsService {
  private isLocalStorageAvailable: boolean;

  constructor(private notificationService: NotificationService) {
    this.isLocalStorageAvailable = this.checkLocalStorageAvailability();
  }

  getFromLocalStorage<T>(key: string, defaultValue?: T): T | null {
    if (!this.isLocalStorageAvailable) {
      console.warn('localStorage is not available');
      return defaultValue || null;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue || null;
      }
      
      try {
        return JSON.parse(item);
      } catch {
        return item as T;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return defaultValue || null;
    }
  }

  setInLocalStorage<T>(key: string, value: T): boolean {
    if (!this.isLocalStorageAvailable) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      return true;
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.notificationService.showWarning('Lagringsutrymme är fullt');
      }
      return false;
    }
  }

  getElementById(id: string): HTMLElement | null {
    try {
      return document.getElementById(id);
    } catch (error) {
      console.error(`Error accessing element with ID '${id}':`, error);
      return null;
    }
  }

  setDocumentAttribute(name: string, value: string): boolean {
    try {
      document.documentElement.setAttribute(name, value);
      return true;
    } catch (error) {
      console.error(`Error setting document attribute '${name}':`, error);
      return false;
    }
  }

  getWindowDimensions(): { width: number; height: number } | null {
    try {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    } catch (error) {
      console.error('Error getting window dimensions:', error);
      return null;
    }
  }

  isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (error) {
      console.error('Error checking iframe status:', error);
      return false;
    }
  }

  postMessageToParent(message: any): boolean {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(message, '*');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error posting message to parent:', error);
      return false;
    }
  }

  private checkLocalStorageAvailability(): boolean {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
} 